/* eslint-disable react/prop-types -- the prop contract is the TypeScript interface below; this is
   the app's only class component (error boundaries must be classes) and react/prop-types cannot see
   the generic Component<Props> type params. */
import { Component, type ReactNode, useEffect } from 'react'

import { clearReloadGuard, decideRecovery, resolveGuardStorage } from './chunk-recovery.js'

interface ChunkLoadBoundaryProps {
  children: ReactNode
  /** Surface shown when recovery is exhausted (chunk still failing after a reload, or a real bug). */
  fallback: ReactNode
  /** Injectable for tests; defaults to a full page reload. */
  reload?: () => void
  /** Injectable for tests; defaults to `sessionStorage` (survives the reload it triggers). */
  storage?: Storage
}

interface ChunkLoadBoundaryState {
  failed: boolean
}

/**
 * Error boundary around the lazy auth screens. A redeploy rotates the content-hashed chunk names,
 * so an old open shell can throw a dynamic-import failure the moment it lazy-loads `AuthScreens` —
 * which, unguarded, is a blank white screen. This catches that and reloads ONCE (picking up the
 * fresh, network-first HTML and its current asset manifest); if the very next load still fails, or
 * the error is an ordinary render bug, it shows the `fallback` instead of looping. The one-shot
 * guard lives in {@link decideRecovery}; {@link ChunkLoadRecovered} disarms it on success.
 */
export class ChunkLoadBoundary extends Component<ChunkLoadBoundaryProps, ChunkLoadBoundaryState> {
  override state: ChunkLoadBoundaryState = { failed: false }

  static getDerivedStateFromError(): ChunkLoadBoundaryState {
    return { failed: true }
  }

  override componentDidCatch(error: Error): void {
    const storage = resolveGuardStorage(this.props.storage)
    if (decideRecovery(storage, error) === 'reload') {
      ;(this.props.reload ?? ((): void => window.location.reload()))()
    }
  }
  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}

/**
 * Rendered inside the same Suspense subtree as the lazy auth screens, so its mount effect runs only
 * once that chunk has actually loaded — the success signal that disarms the one-shot reload guard,
 * re-enabling a single auto-reload for the NEXT deploy. Renders nothing.
 */
export const ChunkLoadRecovered = ({ storage }: { storage?: Storage }): null => {
  useEffect(() => {
    // Block body on purpose: never return the removeItem result — React would treat a truthy
    // non-function as an effect cleanup and crash on unmount.
    clearReloadGuard(resolveGuardStorage(storage))
  }, [storage])
  return null
}
